import { emptyCostBudgetInput, type CostBudgetApi, type CostBudgetInput, type CostBudgetPayload, type CostExpenseScope } from './costBudgetTypes';
import { sameCostExpenseRows } from './costExpenseRecurrence';

export type CostBudgetError = 'LOAD' | 'PREVIEW' | 'SAVE' | 'CONFLICT' | 'ACCESS' | 'EMPTY_PREVIOUS' | null;
export interface CostBudgetState {
  month: string; input: CostBudgetInput; saved: CostBudgetPayload | null; preview: CostBudgetPayload | null;
  loading: boolean; previewing: boolean; saving: boolean; dirty: boolean; error: CostBudgetError;
  expenseScope: CostExpenseScope | null;
}
const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));
export const validCostDecimal = (value: string | null, maximum = 1e9) => value === null || (/^\d+(?:\.\d+)?$/.test(value) && Number.isFinite(Number(value)) && Number(value) <= maximum);
export function validCostDraft(input: CostBudgetInput): boolean {
  const sections = [input.fixed, input.other];
  if (sections.some(section => section.rows.length > 100 || section.rows.some(row => !row.name.trim() || row.name.length > 100 || row.notes.length > 500 || !validCostDecimal(row.amountExact) || (row.endMonth != null && (!/^(?:20\d{2}|2100)-(?:0[1-9]|1[0-2])$/.test(row.endMonth) || row.frequency === 'ONE_TIME'))))) return false;
  if (!validCostDecimal(input.labor.amountExact) || input.labor.profiles.some(profile => !validCostDecimal(profile.hoursExact, 744))) return false;
  return validCostDecimal(input.forecast.portionsExact) && validCostDecimal(input.forecast.salesExact, 1e12) && validCostDecimal(input.forecast.targetMarginPercentExact, 100);
}
export function initialCostBudgetState(month: string): CostBudgetState {
  return { month, input: emptyCostBudgetInput(), saved: null, preview: null, loading: true, previewing: false, saving: false, dirty: false, error: null, expenseScope: null };
}

/** Preview never saves. Only changes to the labor basis refresh the stored labor snapshot. */
export function createCostBudgetController(api: CostBudgetApi, tenant: string, month: string, publish: (state: CostBudgetState) => void) {
  let state = initialCostBudgetState(month), disposed = false, blocked = false, version = 0;
  let loadAbort: AbortController | null = null, previewAbort: AbortController | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const emit = () => { if (!disposed) publish({ ...state }); };
  const cancelPreview = () => { clearTimeout(timer); timer = undefined; previewAbort?.abort(); previewAbort = null; };
  const belongs = (payload: CostBudgetPayload, expectedMonth: string) => payload?.restaurantId === tenant && payload.month === expectedMonth && payload.budget?.restaurantId === tenant && payload.budget.month === expectedMonth && Number.isInteger(payload.budget.revision) && payload.budget.revision >= 0;
  const fail = (error: unknown, fallback: Exclude<CostBudgetError, null>) => {
    if (disposed || blocked) return;
    const status = (error as { status?: number })?.status;
    if ([401, 402, 403].includes(status || 0)) {
      blocked = true; version++; cancelPreview(); loadAbort?.abort();
      state = { ...initialCostBudgetState(state.month), loading: false, error: 'ACCESS' };
    } else state = { ...state, error: status === 409 ? 'CONFLICT' : fallback };
  };
  const accept = (payload: CostBudgetPayload, expectedMonth: string) => {
    if (belongs(payload, expectedMonth)) return true;
    fail({ status: 403 }, 'ACCESS'); return false;
  };
  async function loadMonth(nextMonth: string, discard = false): Promise<boolean> {
    if (disposed || blocked || state.saving || !/^\d{4}-(0[1-9]|1[0-2])$/.test(nextMonth) || (state.dirty && !discard)) return false;
    const ticket = ++version; loadAbort?.abort(); cancelPreview();
    const abort = new AbortController(); loadAbort = abort;
    state = initialCostBudgetState(nextMonth); emit();
    try {
      const payload = await api.load(nextMonth, abort.signal);
      if (disposed || blocked || ticket !== version) return false;
      if (!accept(payload, nextMonth)) return false;
      state = { ...state, input: clone(payload.budget.input), saved: payload, preview: null, dirty: false, error: null };
      return true;
    } catch (error) { if (!disposed && ticket === version && !abort.signal.aborted) fail(error, 'LOAD'); return false; }
    finally { if (!disposed && ticket === version) { state = { ...state, loading: false }; emit(); } else if (blocked) emit(); }
  }
  async function previewNow(): Promise<boolean> {
    clearTimeout(timer); timer = undefined;
    if (disposed || blocked || state.loading || state.saving || !state.saved || !state.dirty || !validCostDraft(state.input)) return false;
    previewAbort?.abort(); const abort = new AbortController(); previewAbort = abort;
    const ticket = version, expectedMonth = state.month, input = clone(state.input);
    state = { ...state, previewing: true, error: null }; emit();
    try {
      const payload = await api.preview(expectedMonth, input, abort.signal);
      if (disposed || blocked || ticket !== version || abort.signal.aborted) return false;
      if (!accept(payload, expectedMonth)) return false;
      state = { ...state, preview: payload, error: null }; return true;
    } catch (error) { if (!disposed && ticket === version && !abort.signal.aborted) fail(error, 'PREVIEW'); return false; }
    finally { if (!disposed && ticket === version && !abort.signal.aborted) { state = { ...state, previewing: false }; emit(); } else if (blocked) emit(); }
  }
  function edit(update: (input: CostBudgetInput) => CostBudgetInput): boolean {
    if (disposed || blocked || state.loading || state.saving || !state.saved) return false;
    const input = update(clone(state.input));
    if (JSON.stringify(input) === JSON.stringify(state.input)) return true;
    version++; cancelPreview();
    const dirty = JSON.stringify(input) !== JSON.stringify(state.saved.budget.input);
    const previewing = dirty && validCostDraft(input);
    state = { ...state, input, dirty, preview: null, previewing, error: null }; emit();
    if (previewing) timer = setTimeout(() => { void previewNow(); }, 350);
    return true;
  }
  async function save(): Promise<boolean> {
    if (disposed || blocked || state.loading || state.saving || state.previewing || !state.saved || !state.dirty || !validCostDraft(state.input)) return false;
    if (state.saved.recurrence && !sameCostExpenseRows(state.saved.budget.input, state.input) && !state.expenseScope) return false;
    cancelPreview(); const ticket = ++version, expectedMonth = state.month;
    const input = clone(state.input), expectedRevision = state.saved.budget.revision;
    state = { ...state, saving: true, error: null }; emit();
    try {
      const options = state.saved!.recurrence ? { scope: state.expenseScope || 'ONLY_MONTH' as const, expectedRecurrenceRevision: state.saved!.recurrence.revision } : undefined;
      const payload = await api.save(expectedMonth, input, expectedRevision, options);
      if (disposed || blocked || ticket !== version) return false;
      if (!accept(payload, expectedMonth)) return false;
      state = { ...state, input: clone(payload.budget.input), saved: payload, preview: null, dirty: false, error: null, expenseScope: null }; return true;
    } catch (error) { if (!disposed && ticket === version) fail(error, 'SAVE'); return false; }
    finally { if (!disposed && ticket === version) { state = { ...state, saving: false }; emit(); } else if (blocked) emit(); }
  }
  async function copyPrevious(discard = false): Promise<boolean> {
    if (disposed || blocked || state.loading || state.saving || !state.saved || state.saved.budget.revision !== 0 || (state.dirty && !discard)) return false;
    const [year, number] = state.month.split('-').map(Number), previousMonth = number === 1 ? `${year - 1}-12` : `${year}-${String(number - 1).padStart(2, '0')}`;
    cancelPreview(); loadAbort?.abort(); const abort = new AbortController(); loadAbort = abort;
    const ticket = ++version; state = { ...state, loading: true, previewing: false, error: null }; emit();
    try {
      const payload = await api.load(previousMonth, abort.signal);
      if (disposed || blocked || ticket !== version) return false;
      if (!accept(payload, previousMonth)) return false;
      if (!payload.budget.revision) { state = { ...state, error: 'EMPTY_PREVIOUS' }; return false; }
      const input = clone(state.input);
      input.fixed = clone(payload.budget.input.fixed); input.other = clone(payload.budget.input.other);
      for (const group of ['fixed', 'other'] as const) { input[group].rows = input[group].rows.filter(row => row.frequency !== 'ONE_TIME'); input[group].reviewed = false; }
      state = { ...state, loading: false }; edit(() => input); return true;
    } catch (error) { if (!disposed && ticket === version && !abort.signal.aborted) fail(error, 'LOAD'); return false; }
    finally { if (!disposed && ticket === version) { state = { ...state, loading: false }; emit(); } else if (blocked) emit(); }
  }
  function setExpenseScope(scope: CostExpenseScope): boolean {
    if (disposed || blocked || state.loading || state.saving || !['ONLY_MONTH', 'FROM_MONTH_FORWARD'].includes(scope)) return false;
    state = { ...state, expenseScope: scope }; emit(); return true;
  }
  return { snapshot: () => state, loadMonth, previewNow, edit, save, copyPrevious, setExpenseScope, dispose() { disposed = true; version++; loadAbort?.abort(); cancelPreview(); } };
}
